// Core
import React from 'react';

// Libraries
import { useTranslation } from 'react-i18next';

// Components
import { Paragraph, ParagraphWeight } from '../../../layout/elements/Paragraph';
import { Select, SelectSizeType } from '../../../layout/elements/Select';

// Store
import {
  MeasurementStore,
  setMeasurementUnit,
  useMeasurementStore,
} from '../../../store/measurement.store';

// Types
import {
  LengthType,
  MeasurementType,
  PressureType,
  TemperatureType,
} from '../../../types/global';

// View
import { MeasurementModuleProps } from './MeasurementModule.types';
import {
  ActionsContainer,
  Container,
  Content,
  TitleContainer,
} from './MeasurementModule.style';
import { showToast, ToastType } from '../../../layout/elements/Toast';

function MeasurementModule({
  className,
  testId,
}: MeasurementModuleProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();

  const selectedTemperature: TemperatureType = useMeasurementStore((
    state: MeasurementStore,
  ) => state.temperature);

  const selectedPressure: PressureType = useMeasurementStore((
    state: MeasurementStore,
  ) => state.pressure);

  const selectedLength: LengthType = useMeasurementStore((
    state: MeasurementStore,
  ) => state.length);

  /* Handles the change of the measurement unit */
  const handleUnitChange = (
    value: TemperatureType | PressureType | LengthType,
    measurementUnit: MeasurementType,
  ): void => {
    setMeasurementUnit(value, measurementUnit);

    showToast({
      text: t('view.settings.measurementModule.toastDescription'),
      type: ToastType.success,
    });
  };

  return (
    <Container
      className={className}
      testId={testId}
      xs={24}
      lg={12}
    >
      <Content>
        <TitleContainer>
          <Paragraph
            weight={ParagraphWeight.bold}
          >
            {t('view.settings.measurementModule.title')}
          </Paragraph>
        </TitleContainer>
        <ActionsContainer>
          <Select
            testId="temperature-select-measurement-module"
            label={t('common.temperature')}
            placeholder={t('common.temperature')}
            size={SelectSizeType.lg}
            items={[
              { text: 'Celsius', id: TemperatureType.c },
              { text: 'Fahrenheit', id: TemperatureType.f },
            ]}
            value={selectedTemperature}
            onChange={(value): void => handleUnitChange(
              value as TemperatureType,
              MeasurementType.temperature,
            )}
            allowClear={false}
          />
          <Select
            testId="length-select-measurement-module"
            label={t('common.length')}
            placeholder={t('common.length')}
            size={SelectSizeType.lg}
            items={[
              { text: t('common.measurement.millimeter'), id: LengthType.mm },
              { text: t('common.measurement.inch'), id: LengthType.inch },
            ]}
            value={selectedLength}
            onChange={(value): void => handleUnitChange(
              value as LengthType,
              MeasurementType.length,
            )}
            allowClear={false}
          />
          <Select
            testId="pressure-select-measurement-module"
            label={t('common.pressure')}
            placeholder={t('common.pressure')}
            size={SelectSizeType.lg}
            items={[
              { text: t('common.measurement.megaPascal'), id: PressureType.MPa },
              { text: PressureType.bar, id: PressureType.bar },
              { text: PressureType.PSI, id: PressureType.PSI },
            ]}
            value={selectedPressure}
            onChange={(value): void => handleUnitChange(
              value as PressureType,
              MeasurementType.pressure,
            )}
            allowClear={false}
          />
        </ActionsContainer>
      </Content>
    </Container>
  );
}

export { MeasurementModule };
